/**
 * AdminErrorBoundary.js
 * ─────────────────────────────────────────────────────────────────────
 * React class-component error boundary wrapping the entire admin
 * subtree. Catches:
 *
 *   • Render errors thrown by any descendant.
 *   • Lazy-chunk load failures (React.lazy + Suspense surface these
 *     as render errors in the Suspense boundary's owner).
 *   • Async errors that bubble synchronously back through React's
 *     scheduler (most thrown errors during effects).
 *
 * Does NOT catch:
 *   • Async/promise rejections inside useEffect — those land on
 *     window.unhandledrejection. AdminApiClient logs those at the
 *     fetch boundary; render-time errors are covered here.
 *
 * MUST be eagerly imported (not via React.lazy) in App.js — otherwise
 * a lazy-load failure of the boundary itself would never be caught.
 *
 * Fallback UI shows the last X-Request-ID from any in-flight admin
 * API call (read from a global slot AdminApiClient sets). Admins can
 * quote it to ops for traceback correlation.
 */

import React from "react";
import { adminLogger } from "./AdminLogger";

// We could pull the last request_id from a module-level slot in
// AdminApiClient; instead we read it from `window.__fm_last_request_id`
// which AdminApiClient sets on every response. Keeps the boundary
// import-free of the API client (the boundary HAS to load first; the
// API client loads with the lazy chunk).
const lastRequestId = () => {
  if (typeof window === "undefined") return null;
  return window.__fm_last_request_id || null;
};

export default class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, requestId: null };
  }

  static getDerivedStateFromError(error) {
    return { error, requestId: lastRequestId() };
  }

  componentDidCatch(error, info) {
    // Stringify only what we can safely log. Don't ship full component
    // stacks — they can contain props that contain PII.
    adminLogger.error("admin_error_boundary_caught", {
      message: String(error?.message || error),
      stack:   typeof error?.stack === "string" ? error.stack.slice(0, 2000) : null,
      requestId: this.state.requestId,
      // info.componentStack is React-internal. Truncate hard.
      componentStack: typeof info?.componentStack === "string"
        ? info.componentStack.slice(0, 1000)
        : null,
    });
  }

  handleReload = () => {
    // Full page reload — sidesteps any wedged React state. The session
    // re-validates as part of AdminAuthGuard's mount, which handles
    // the "session went bad" case naturally.
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="fm-admin-error-boundary" role="alert">
        <div className="fm-admin-error-boundary__panel">
          <div className="fm-admin-error-boundary__icon" aria-hidden="true">⚠</div>
          <h1 className="fm-admin-error-boundary__title">Something went wrong</h1>
          <p className="fm-admin-error-boundary__msg">
            The admin panel ran into an unexpected error. The page has been
            stopped to prevent further issues.
          </p>
          {this.state.requestId && (
            <p className="fm-admin-error-boundary__ref">
              Reference ID:{" "}
              <code className="fm-admin-error-boundary__rid">{this.state.requestId}</code>
            </p>
          )}
          <div className="fm-admin-error-boundary__actions">
            <button
              type="button"
              className="fm-admin-btn fm-admin-btn--primary"
              onClick={this.handleReload}
            >
              Reload
            </button>
            <a className="fm-admin-btn fm-admin-btn--ghost" href="/admin/login">
              Sign in again
            </a>
          </div>
        </div>
      </div>
    );
  }
}
